import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { AddPackageCustomerRequest, PackageCustomerResponse } from "./packagesApi";
import { addPackageCustomer, deletePackageCustomer, listPackageCustomers } from "./packagesApi";

export const usePackageCustomers = (packageId: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useQuery({
    queryKey: ["packages", packageId, "customers"],
    queryFn: ({ signal }) => listPackageCustomers(packageId, signal),
  });

  const addPackageCustomerMutation = useMutation<PackageCustomerResponse, unknown, AddPackageCustomerRequest>({
    mutationFn: (r) => addPackageCustomer(packageId, r),
    onSuccess: async (data) => {
      enqueueSnackbar(`Klient ${data.name} został dodany do pakietu`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["packages", packageId, "customers"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać klienta. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deletePackageCustomerMutation = useMutation<void, unknown, { customerId: string }>({
    mutationFn: ({ customerId }) => deletePackageCustomer(packageId, customerId),
    onSuccess: async () => {
      enqueueSnackbar("Klient został usunięty z pakietu", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["packages", packageId, "customers"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć klienta. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    customers: data ?? [],
    isLoading,
    addPackageCustomer: (request: AddPackageCustomerRequest) => addPackageCustomerMutation.mutateAsync(request),
    deletePackageCustomer: (customerId: string) => deletePackageCustomerMutation.mutateAsync({ customerId }),
  };
};
